/**
 * Wires the offline engine to the real browser store and the real API. One per page; created on first use.
 * Kept out of engine.ts so the engine stays testable without a network or a browser.
 */
import { api } from "../api.js";
import { createClock } from "./clock.js";
import { registerOfflineQueue, registerReplicaReader } from "./connectivity.js";
import { type EngineState, type OfflineApi, OfflineEngine } from "./engine.js";
import { idbStore } from "./kv.js";
import { replicaAnswer } from "./replica-read.js";
import { Vault } from "./vault.js";

export interface ServerConflict {
  id: string;
  batch_id: string;
  batch_number: string;
  device_id: string;
  existing_value: string;
  offline_value: string;
  material: boolean;
  exception_item_id: string | null;
  exception_state: string | null;
  opened_at_server: string;
  status: "OPEN" | "ADJUDICATED" | "NOT_MATERIAL";
}

export interface ServerLocalAudit {
  id: string;
  audit_id: string;
  before_payload: Record<string, unknown>;
  after_payload: Record<string, unknown>;
  reason: string;
  actor_name: string;
  device_id: string;
  device_time: string | null;
  received_at_server: string;
}

const BASE = "/api/v1/offline";

const serverApi: OfflineApi = {
  anchor: (b) => api.post(`${BASE}/anchors`, b),
  openSession: (b) => api.post(`${BASE}/sync-sessions`, b),
  sync: (b) => api.post(`${BASE}/sync`, b),
  closeSession: (id) => api.post(`${BASE}/sync-sessions/${id}/close`, {}),
  blockingTasks: (batchId) => api.get(`${BASE}/batches/${batchId}/blocking-tasks`),
  resolveRejection: (id, resolution) => api.post(`${BASE}/rejections/${id}/resolve`, { resolution }),
  workingSet: (known) => api.get(`${BASE}/working-set${known ? `?known=${encodeURIComponent(known)}` : ""}`),
  reportLost: (deviceId, circumstances) =>
    api.post(`${BASE}/devices/${encodeURIComponent(deviceId)}/report-lost`, { circumstances }),
};

export const offlineEndpoints = {
  conflicts: (batchId: string) => api.get<ServerConflict[]>(`${BASE}/batches/${batchId}/conflicts`),
  localAudit: (batchId: string) => api.get<ServerLocalAudit[]>(`${BASE}/batches/${batchId}/local-audit`),
};

let signedInUser: string | null = null;
export const setSignedInUser = (id: string | null): void => {
  signedInUser = id;
};

let engine: Promise<OfflineEngine> | null = null;

export function getEngine(): Promise<OfflineEngine> {
  engine ??= (async () => {
    const kv = idbStore();
    const e = new OfflineEngine({
      vault: new Vault(kv),
      clock: await createClock(kv),
      api: serverApi,
      currentUserId: () => signedInUser,
    });
    await e.init();
    registerReplicaReader((path) => replicaAnswer(e.getState().workingSet, path));
    // api.ts hands a capture here whenever the server cannot be reached
    registerOfflineQueue(async (type, batchId, body) => {
      const { clientCommandId, expectedVersion: _stale, ...payload } = body;
      await e.capture(
        type,
        batchId,
        payload,
        typeof clientCommandId === "string" ? clientCommandId : undefined,
      );
      return { queued: true, clientCommandId };
    });
    return e;
  })();
  return engine;
}

/** The device id this browser profile registers under; stable across reloads. */
export const newDeviceId = (): string => `tablet-${crypto.randomUUID().slice(0, 8)}`;

export type { EngineState };
